import { useState, type FormEvent } from "react";
import { AnimatePresence, motion } from "motion/react";
import { currencySchema, dayKey, isLive, makeId, nextProjectColor, planDelivery, type BillingMode, type DeliveryPlan, type ProjectColor, type SyncOperation } from "@otrack/domain";
import { Button, DateField, Field, FieldError, IconButton, Modal, Segmented, Select, Switch, TextInput, TimeField } from "@otrack/ui";
import { ColorPicker, currencyOptions, parseMoneyInput } from "./components";
import { useApp, useDataset, useFactory } from "./context";
import { navigate } from "./router";

const NEW_CLIENT = "__new__";

/** "16:30" as minutes after midnight; null when it is not a time. */
function minutesOf(value: string): number | null {
  const match = /^(\d{2}):(\d{2})$/.exec(value);
  return match ? Number(match[1]) * 60 + Number(match[2]) : null;
}

const reveal = {
  initial: { opacity: 0, height: 0 },
  animate: { opacity: 1, height: "auto" },
  exit: { opacity: 0, height: 0 },
  transition: { duration: 0.3, ease: [0.16, 1, 0.3, 1] as const },
  style: { overflow: "hidden" as const }
};

export function ProjectEditor({ onClose, clientId: initialClientId }: { onClose: () => void; clientId?: string | null | undefined }) {
  const { act } = useApp();
  const dataset = useDataset();
  const factory = useFactory();
  const clients = Object.values(dataset.clients).filter((client) => isLive(client) && client.archivedAt === null).sort((a, b) => a.name.localeCompare(b.name));
  const [clientId, setClientId] = useState(() => (initialClientId && clients.some((client) => client.id === initialClientId) ? initialClientId : clients.length ? "" : NEW_CLIENT));
  const [clientName, setClientName] = useState("");
  const [name, setName] = useState("");
  const [mode, setMode] = useState<BillingMode>("hourly");
  const [color, setColor] = useState<ProjectColor>(() => nextProjectColor(dataset));
  const client = clientId && clientId !== NEW_CLIENT ? dataset.clients[clientId] : undefined;
  const inheritedCurrency = client?.defaultCurrency ?? dataset.settings.defaultCurrency;
  const [currency, setCurrency] = useState(inheritedCurrency);
  const [amount, setAmount] = useState("");
  const [deadline, setDeadline] = useState<number | null>(null);
  const [delivery, setDelivery] = useState(true);
  const [deliveryWhen, setDeliveryWhen] = useState<"all-day" | "time">("all-day");
  const [deliveryStart, setDeliveryStart] = useState("16:00");
  const [deliveryEnd, setDeliveryEnd] = useState("17:00");
  const [error, setError] = useState<{ field: "name" | "client" | "amount" | "delivery"; message: string } | null>(null);

  const chooseClient = (id: string) => {
    setClientId(id);
    const next = id && id !== NEW_CLIENT ? dataset.clients[id] : undefined;
    setCurrency(next?.defaultCurrency ?? dataset.settings.defaultCurrency);
    if (error?.field === "client") setError(null);
  };

  const save = async (event: FormEvent) => {
    event.preventDefault();
    if (clientId === NEW_CLIENT && !clientName.trim()) return setError({ field: "client", message: "Name the new client, or pick No client." });
    if (!name.trim()) return setError({ field: "name", message: "Name the project." });
    const parsedCurrency = currencySchema.safeParse(currency.trim().toUpperCase());
    if (!parsedCurrency.success) return setError({ field: "amount", message: "Use a three-letter currency code, like USD." });
    const minor = mode === "non-billable" ? null : parseMoneyInput(amount, parsedCurrency.data);
    if (minor === undefined) return setError({ field: "amount", message: "Enter a positive amount, like 45 or 45.50." });
    let plan: DeliveryPlan | null = null;
    if (deadline !== null && delivery) {
      const startMinutes = minutesOf(deliveryStart);
      const endMinutes = minutesOf(deliveryEnd);
      if (deliveryWhen === "time" && (startMinutes === null || endMinutes === null)) return setError({ field: "delivery", message: "Enter a start and an end time." });
      if (deliveryWhen === "time" && endMinutes! <= startMinutes!) return setError({ field: "delivery", message: "The delivery ends before it starts." });
      plan = deliveryWhen === "all-day" ? { allDay: true } : { allDay: false, startMinutes: startMinutes!, endMinutes: endMinutes! };
    }

    const operations: SyncOperation[] = [];
    let resolvedClientId: string | null = clientId || null;
    let clientCurrency = client?.defaultCurrency ?? dataset.settings.defaultCurrency;
    if (clientId === NEW_CLIENT) {
      resolvedClientId = makeId("client");
      clientCurrency = parsedCurrency.data;
      operations.push(factory.op("client", resolvedClientId, { name: clientName.trim(), defaultCurrency: parsedCurrency.data }));
    }
    const projectId = makeId("project");
    operations.push(factory.op("project", projectId, {
      name: name.trim(),
      color,
      clientId: resolvedClientId,
      billingMode: mode,
      currency: parsedCurrency.data === clientCurrency ? null : parsedCurrency.data,
      hourlyRate: mode === "hourly" ? minor : null,
      fixedPrice: mode === "fixed" ? minor : null,
      deadline: deadline === null ? null : dayKey(deadline)
    }));
    if (deadline !== null && plan) operations.push(...planDelivery({ id: projectId, name: name.trim() }, dayKey(deadline), plan, factory).operations);
    await act(operations, `Created ${name.trim()}`);
    onClose();
    if (/^#\/(projects|client)/.test(window.location.hash)) navigate({ page: "project", id: projectId });
  };

  return <Modal title="New project" icon="folder" description="A body of work you track time against." onClose={onClose}>
    <form className="ot-form" onSubmit={(event) => void save(event)} noValidate>
      <Field label="Client">
        <Select
          value={clientId}
          searchable={clients.length > 7}
          onChange={chooseClient}
          options={[
            { value: "", label: "No client", icon: "folder" },
            ...clients.map((item) => ({ value: item.id, label: item.name, icon: "user" as const, hint: item.defaultCurrency ?? undefined })),
            { value: NEW_CLIENT, label: "New client…", icon: "plus", divider: clients.length > 0 }
          ]}
        />
      </Field>
      <AnimatePresence initial={false}>
        {clientId === NEW_CLIENT && <motion.div key="client" {...reveal}>
          <Field label="Client name" error={error?.field === "client" ? error.message : null}>
            <TextInput value={clientName} onChange={(event) => { setClientName(event.target.value); if (error?.field === "client") setError(null); }} maxLength={200} placeholder="Who pays for this work" autoFocus/>
          </Field>
        </motion.div>}
      </AnimatePresence>
      <Field label="Project name" error={error?.field === "name" ? error.message : null}>
        <TextInput value={name} onChange={(event) => { setName(event.target.value); if (error?.field === "name") setError(null); }} maxLength={200} placeholder="e.g. Checkout rebuild" autoFocus={clientId !== NEW_CLIENT}/>
      </Field>
      <Field label="Color">
        <ColorPicker value={color} onChange={setColor}/>
      </Field>
      <Field label="Billing">
        <Segmented stretch value={mode} onChange={setMode} options={[{ value: "hourly", label: "Hourly" }, { value: "fixed", label: "Fixed price" }, { value: "non-billable", label: "Non-billable" }]}/>
      </Field>
      <div className="ot-form__pair">
        <Field label="Currency">
          <Select value={currency} onChange={setCurrency} options={currencyOptions([inheritedCurrency, currency])}/>
        </Field>
        <AnimatePresence initial={false} mode="popLayout">
          {mode !== "non-billable" && <motion.div key={mode} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.22 }}>
            <Field label={mode === "hourly" ? "Hourly rate" : "Fixed price"} optional error={error?.field === "amount" ? error.message : null}>
              <TextInput
                inputMode="decimal"
                value={amount}
                onChange={(event) => { setAmount(event.target.value); if (error?.field === "amount") setError(null); }}
                placeholder={mode === "hourly" ? "Client's rate" : "Per task"}
                trailing={<span className="ot-unit">{currency}{mode === "hourly" ? <span>/h</span> : null}</span>}
              />
            </Field>
          </motion.div>}
        </AnimatePresence>
      </div>
      <Field label="Deadline" optional>
        <div className="ot-deadline">
          <DateField aria-label="Deadline" value={deadline} onChange={setDeadline}/>
          {deadline !== null && <IconButton type="button" label="Clear deadline" icon="close" onClick={() => setDeadline(null)}/>}
        </div>
      </Field>
      <AnimatePresence initial={false}>
        {deadline !== null && <motion.div key="delivery" {...reveal}>
          <div className="ot-form__card ot-stack">
            <Switch label="Plan a delivery event" description="Puts the handoff on the Calendar. It moves when the deadline does." checked={delivery} onChange={setDelivery}/>
            {delivery && <>
              <Segmented stretch aria-label="Delivery time" value={deliveryWhen} onChange={setDeliveryWhen} options={[{ value: "all-day", label: "All day" }, { value: "time", label: "At a time" }]}/>
              {deliveryWhen === "time" && <div className="ot-form__pair">
                <Field label="Start"><TimeField value={deliveryStart} placeholder="16:00" invalid={error?.field === "delivery"} onChange={(value) => { setDeliveryStart(value); if (error?.field === "delivery") setError(null); }}/></Field>
                <Field label="End"><TimeField value={deliveryEnd} placeholder="17:00" invalid={error?.field === "delivery"} onChange={(value) => { setDeliveryEnd(value); if (error?.field === "delivery") setError(null); }}/></Field>
              </div>}
              <FieldError>{error?.field === "delivery" ? error.message : null}</FieldError>
            </>}
          </div>
        </motion.div>}
      </AnimatePresence>
      <div className="ot-form__actions">
        <span className="ot-form__spacer"/>
        <Button type="button" onClick={onClose}>Cancel</Button>
        <Button type="submit" tone="primary" icon="plus">Create project</Button>
      </div>
    </form>
  </Modal>;
}
